import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import AddIcon from "@mui/icons-material/Add";
import { useTaskCreation } from "./useTaskCreation";
import { UrlGroupCard } from "./UrlGroupCard";
import { BulkEstimateSummary } from "./BulkEstimateSummary";
import { RegionSelect } from "./RegionSelect";
import { StructureParamsSection } from "./StructureParamsSection";
import { PublicationParamsSection } from "./PublicationParamsSection";

const LANGUAGE_OPTIONS = [
  { value: "English", label: "English" },
  { value: "Italian", label: "Italian" },
  { value: "French", label: "French" },
  { value: "German", label: "German" },
  { value: "Dutch", label: "Dutch" },
  { value: "Polish", label: "Polish" },
] as const;

const TASK_TYPE_OPTIONS = [
  { value: "generation_anchor_topic", label: "Anchor topic" },
  { value: "generation_relevant_topic", label: "Relevant topic" },
  { value: "generation_relevant_topic_fixed", label: "Relevant topic (Fixed)" },
  { value: "homepage_publication", label: "Homepage publication" },
  { value: "sitewide_publication", label: "Sitewide (на всех страницах)" },
] as const;

export function TaskCreation() {
  const {
    shared,
    groups,
    screenState,
    regionsState,
    topicsState,
    setSharedField,
    setStructureParameter,
    setPublicationParameter,
    addGroup,
    removeGroup,
    setGroupField,
    setGroupAnchorRow,
    setGroupUniqueArticlesCount,
    setGroupTopicRow,
    processGroupAnchors,
    resetGroupAnchorsToTextarea,
    submit,
    retryRegions,
    retryTopics,
  } = useTaskCreation();

  const isSubmitting = screenState.kind === "submitting";
  const fieldErrors = screenState.kind === "error" ? screenState.fieldErrors : {};
  const groupErrorsByIndex = screenState.kind === "error" ? screenState.groupErrors : {};

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    submit();
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Создание задач</Typography>

      <form onSubmit={handleSubmit}>
        <Box maxWidth={640}>
          <TextField
            select
            label="Тип задачи"
            value={shared.taskType}
            disabled={isSubmitting}
            onChange={(event) => setSharedField("taskType", event.target.value as typeof shared.taskType)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            {TASK_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>

          {shared.taskType === "sitewide_publication" && (
            <Box sx={{ mb: 2, color: "text.secondary", fontSize: 13 }}>
              Ссылка будет размещена в footer и будет отображаться на всех страницах сайта
            </Box>
          )}

          <TextField
            select
            label="Язык"
            value={shared.language}
            error={!!fieldErrors.language}
            helperText={fieldErrors.language}
            disabled={isSubmitting}
            onChange={(event) => setSharedField("language", event.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>

          <RegionSelect
            regionsState={regionsState}
            value={shared.region}
            error={fieldErrors.region}
            disabled={isSubmitting}
            onChange={(value) => setSharedField("region", value)}
            onRetry={retryRegions}
          />

          {shared.taskType !== "homepage_publication" && shared.taskType !== "sitewide_publication" && (
            <StructureParamsSection
              params={shared.structureParams}
              fieldErrors={fieldErrors}
              disabled={isSubmitting}
              onChange={setStructureParameter}
            />
          )}
        </Box>

        {groups.map((group, index) => (
          <UrlGroupCard
            key={index}
            index={index}
            group={group}
            taskType={shared.taskType}
            isOnlyGroup={groups.length === 1}
            topicsState={topicsState}
            groupErrors={groupErrorsByIndex[index] ?? {}}
            disabled={isSubmitting}
            onRemove={() => removeGroup(index)}
            onChangeField={(key, value) => setGroupField(index, key, value)}
            onProcess={() => processGroupAnchors(index)}
            onResetToTextarea={() => resetGroupAnchorsToTextarea(index)}
            onRetryTopics={retryTopics}
            onSetAnchorRow={(anchorIndex, field, value) =>
              setGroupAnchorRow(index, anchorIndex, field, value)
            }
            onSetUniqueArticlesCount={(value) => setGroupUniqueArticlesCount(index, value)}
            onSetTopicRow={(topicIndex, value) => setGroupTopicRow(index, topicIndex, value)}
          />
        ))}

        <Box maxWidth={640} sx={{ mb: 2 }}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            disabled={isSubmitting}
            onClick={addGroup}
          >
            Добавить URL-группу
          </Button>
        </Box>

        <BulkEstimateSummary taskType={shared.taskType} groups={groups} />

        <Box maxWidth={640}>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={shared.autoPublish}
                  disabled={isSubmitting}
                  onChange={(event) => setSharedField("autoPublish", event.target.checked)}
                />
              }
              label="Автозапуск публикации"
            />
            <Box sx={{ ml: 4, color: "text.secondary", fontSize: 13 }}>
              Публикация запустится автоматически после генерации с параметрами ниже
            </Box>
          </Box>

          {shared.autoPublish && (
            <PublicationParamsSection
              value={shared.publicationParams}
              regionsState={regionsState}
              disabled={isSubmitting}
              onChange={setPublicationParameter}
            />
          )}

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{ mt: 2 }}
          >
            {isSubmitting ? "Создание…" : "Создать задачи"}
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export type NumericAnchorField = "articleCount" | "uniqueness";

export type NumericParamKey = "listCount" | "tableCount" | "faqQuestionCount";
